import { MonthlyRepetitionType, RepetitionPattern, IRepetitionPatternFields } from '../RepetitionPattern';
import { RepetitionEnding, DEFAULT_NUM_OCCURRENCES, IRepetitionEnding as DeprecatedIRepetitionEnding } from '../RepetitionEnding';
import { addDays, format } from 'date-fns';
import RepetitionPatternMapper from '../../../logic/RepetitionPatternLogic';
import { convertTimestampFromCurrentTimeZoneToTimeZone } from '../../../logic/TimeZones';
import { DaysOfTheWeek, IRepetitionEnding, IRepetitionPattern, RepetitionEndType, RepetitionFrequency } from '../interfaces/IRepetitionPattern';

export const isServerRepetitionPattern = (x: any): x is IServerRepetitionPattern => {
    if (typeof x === 'object') {
        return (x as { monthlyRepetitionPattern: MonthlyRepetitionType }).monthlyRepetitionPattern === undefined;
    } else {
        return false;
    }
}

export interface IServerRepetitionPattern {
    startTimestamp: Date;
    endTimestamp: Date;
    timeZone: string;
    frequency: string;
    interval: number;
    daysOfTheWeek: string[];
    endType: string;
    repetitionEndDaysFromStart?: number;
    occurrences?: number;
}

export class ServerRepetitionPattern implements IServerRepetitionPattern {
    startTimestamp: Date;
    endTimestamp: Date;
    timeZone: string;
    frequency: string;
    interval: number;
    daysOfTheWeek: string[];
    endType: string;
    repetitionEndDaysFromStart?: number;
    occurrences?: number;

    constructor(clientOrServerObject: RepetitionPattern | IServerRepetitionPattern) {
        if (clientOrServerObject.startTimestamp && clientOrServerObject.endTimestamp && clientOrServerObject.timeZone) {
            this.interval = clientOrServerObject.interval;
            this.timeZone = clientOrServerObject.timeZone;
            if (isServerRepetitionPattern(clientOrServerObject)) {
                this.startTimestamp = new Date(clientOrServerObject.startTimestamp);
                this.endTimestamp = new Date(clientOrServerObject.endTimestamp);
                this.endType = clientOrServerObject.endType;
                this.repetitionEndDaysFromStart = clientOrServerObject.repetitionEndDaysFromStart;
                this.occurrences = clientOrServerObject.occurrences;
                this.frequency = clientOrServerObject.frequency;
                this.daysOfTheWeek = clientOrServerObject.daysOfTheWeek;
            } else {
                // For proper UTC storage of the timestamps, calculate the timestamp in the current time zone
                this.startTimestamp = clientOrServerObject.startTimestamp!;
                this.endTimestamp = clientOrServerObject.endTimestamp!;
                const ending = clientOrServerObject.ending;
                this.endType = ending.endType;
                this.repetitionEndDaysFromStart = ending.endType === RepetitionEndType.EndDate
                    ? clientOrServerObject.endingEndDateDaysFromStartDate
                    : undefined;
                this.occurrences = ending.endType === RepetitionEndType.AfterOccurrences ? clientOrServerObject.ending.numOccurrences : undefined;
                this.frequency = clientOrServerObject.isCustomPattern ? clientOrServerObject.customFrequency : clientOrServerObject.frequency;
                this.daysOfTheWeek = clientOrServerObject.isWeeklyPattern ? clientOrServerObject.daysOfTheWeek : [];
            }
        } else {
            throw (new Error('Invalid start or end to repetition pattern or missing time zone.'));
        }
    }

    setStartTimestamp(timestamp: Date) {
        this.startTimestamp = new Date(timestamp);
    }

    setEndTimestamp(timestamp: Date) {
        this.endTimestamp = new Date(timestamp);
    }

    // deserialize(): IRepetitionPattern {
    //     const startTimestampInTimeZone = convertTimestampFromCurrentTimeZoneToTimeZone(this.startTimestamp, this.timeZone);
    //     let repetitionEnding: IRepetitionEnding;
    //     const repetitionEndType = RepetitionEndType[this.endType as RepetitionEndType];
    //     switch (repetitionEndType) {
    //         case RepetitionEndType.AfterOccurrences:
    //             repetitionEnding = { endType: repetitionEndType, numOccurrences: this.occurrences! };
    //             break;
    //         case RepetitionEndType.EndDate:
    //             repetitionEnding = { endType: repetitionEndType, endDate: addDays(startTimestampInTimeZone, this.repetitionEndDaysFromStart!) };
    //             break;
    //         default:
    //             repetitionEnding = { endType: repetitionEndType };
    //             break;
    //     }
    //     // const repetitionEndingData: IRepetitionEnding = {
    //     //     endType: RepetitionEndType[this.endType as RepetitionEndType],
    //     //     endDate: this.repetitionEndDaysFromStart ? addDays(startTimestampInTimeZone, this.repetitionEndDaysFromStart) : null,
    //     //     numOccurrences: this.occurrences || DEFAULT_NUM_OCCURRENCES
    //     // }
    //     const repetitionPattern: IRepetitionPattern = {
    //         startTimestamp: this.startTimestamp,
    //         endTimestamp: this.endTimestamp,
    //         timeZone: this.timeZone,
    //         frequency: this.determineClientFrequency(),
    //         interval: this.interval,
    //         // customFrequencyDescriptor: this.getCustomFrequencyDescriptor(),
    //         daysOfTheWeek: this.daysOfTheWeek as DaysOfTheWeek[],
    //         // monthlyRepetitionPattern: this.isMonthlyRepetitionPattern() ? this.frequency as MonthlyRepetitionType : RepetitionFrequency.MonthlyOnDayOfWeek,
    //         ending: repetitionEnding
    //         // new RepetitionEnding(repetitionEndingData),
    //     }
    //     return repetitionPattern;
    // }

    deprecatedDeserialize(): RepetitionPattern {
        const startTimestampInTimeZone = convertTimestampFromCurrentTimeZoneToTimeZone(this.startTimestamp, this.timeZone);
        const repetitionEndingData: DeprecatedIRepetitionEnding = {
            endType: RepetitionEndType[this.endType as RepetitionEndType],
            endDate: this.repetitionEndDaysFromStart ? addDays(startTimestampInTimeZone, this.repetitionEndDaysFromStart) : null,
            numOccurrences: this.occurrences || DEFAULT_NUM_OCCURRENCES
        }
        const repetitionPatternData: IRepetitionPatternFields = {
            startDate: this.startTimestamp,
            startTime: this.startTimestamp,
            endDate: this.endTimestamp,
            endTime: this.endTimestamp,
            timeZone: this.timeZone,
            frequency: this.determineClientFrequency(),
            interval: this.interval,
            customFrequencyDescriptor: this.getCustomFrequencyDescriptor(),
            daysOfTheWeek: this.daysOfTheWeek as DaysOfTheWeek[],
            monthlyRepetitionPattern: this.isMonthlyRepetitionPattern() ? this.frequency as MonthlyRepetitionType : RepetitionFrequency.MonthlyOnDayOfWeek,
            ending: new RepetitionEnding(repetitionEndingData),
        }
        return new RepetitionPattern(repetitionPatternData);
    }

    private isMonthlyRepetitionPattern() {
        return this.frequency === RepetitionFrequency.MonthlyOnDate
            || this.frequency === RepetitionFrequency.MonthlyOnDayOfWeek;
    }

    private getCustomFrequencyDescriptor() {
        const frequency = this.frequency as RepetitionFrequency;
        if (this.interval === 1) {
            return RepetitionPatternMapper.getSingularFrequencyDescriptor(frequency);
        } else {
            return RepetitionPatternMapper.getPluralFrequencyDescriptor(frequency);
        }
    }

    private determineClientFrequency(): RepetitionFrequency {
        if (this.isCustomFrequency()) {
            return RepetitionFrequency.Custom;
        } else {
            return RepetitionFrequency[this.frequency as RepetitionFrequency];
        }
    }

    private isCustomFrequency() {
        return this.interval !== 1
            || (this.frequency === RepetitionFrequency.Weekly &&
                (this.daysOfTheWeek.length > 1 || !this.daysOfTheWeekContainsStartDay()));
    }

    private daysOfTheWeekContainsStartDay() {
        return this.daysOfTheWeek.indexOf(this.startDayOfWeek()) !== -1;
    }

    private startDayOfWeek() {
        return format(this.startTimestamp, "EEEE");
    }
}